import { useState, type ComponentType, type ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { isMiniappFolder } from '@papan/types'
import { useTranslation } from '@papan/i18n'
import { partitionRefusalKey } from '../protectedBranchFlow'
import { moveErrorText } from '../moveErrorText'
import { withMoveProgress } from '../moveProgress'
import { getActiveTenantId } from '@papan/api-client'
import { useConfirm, useUndo, useToast } from '@papan/core-ui/ui'
import { ShareLinksDialog } from '@papan/share-links/ui'
import { ShareDialog } from '@papan/org-connections/ui'
import { filesApi } from '@papan/files/client'
import { WhoHasAccessDialog } from '../WhoHasAccessDialog'
import { ShareInternallyDialog } from '../ShareInternallyDialog'
import { FileInfoDialog } from '../FileInfoDialog'
import { DebugLogViewer } from '../DebugLogViewer'
import { MiniappIconEditorDialog } from '../MiniappIconEditorDialog'
import type { FileItem } from '../useFiles'
import { useDeleteRunner } from '../useDeleteRunner'
import { projectDisplayName, projectKeys, type ProjectItem } from './data'

/** Destination folder picker — injected (not imported) to avoid a files → workspace edge. */
export type FolderPicker = ComponentType<{
  tenantId: string
  onSelect: (folderId: string) => void
  onClose: () => void
  excludeIds?: string[]
  title?: string
  confirmLabel?: string
}>

export interface ProjectCardActionHandlers {
  onShare: (p: ProjectItem) => void
  onShareOrg: (p: ProjectItem) => void
  onWhoHasAccess: (p: ProjectItem) => void
  /** An org only — a Personal drive has no members, roles or teams to share with. */
  onShareInternally?: (p: ProjectItem) => void
  onRename: (p: ProjectItem) => void
  onDuplicate: (p: ProjectItem) => void
  onDownload: (p: ProjectItem) => void
  onDetails: (p: ProjectItem) => void
  /** Resolves once the delete is settled (or the confirm was declined). */
  onDelete: (p: ProjectItem) => Promise<void>
  onDebugLog: (p: ProjectItem) => void
  onEditIcon: (p: ProjectItem) => void
  onMoveTo?: (p: ProjectItem) => void
  onCopyTo?: (p: ProjectItem) => void
}

const asFile = (p: ProjectItem): FileItem => ({ id: p.folderId, name: p.name, isFolder: true, updatedAt: p.updatedAt } as FileItem)

/**
 * The project/mini-app card's context-menu actions + the dialogs they open —
 * shared by the Projects/MiniApps page AND the drive Home "Suggested" strip so
 * both surfaces get the same rich menu (share, share-to-drive, who-has-access,
 * rename, move/copy, download, details, debug log, delete). Mirrors DriveView's
 * handlers; the list refreshes via `projectKeys` invalidation.
 *
 * The caller owns navigation (onOpen/onBrowse/onEditSettings); this owns the
 * heavy dialogs. Render `dialogs` once somewhere in the caller's tree. `open`: one of
 * them is open (a caller whose page would otherwise keep focus behind it can lock it).
 */
export function useProjectCardActions({ isOrg, FolderPickerDialog, onChanged }: {
  isOrg?: boolean
  FolderPickerDialog?: FolderPicker
  /** After any change these actions make (a rename, a delete, a move…): a caller with its own lists of these apps re-reads them. */
  onChanged?: () => void
}): { handlers: ProjectCardActionHandlers; dialogs: ReactNode; open: boolean } {
  const { t } = useTranslation('files')
  const confirm = useConfirm()
  const undo = useUndo()
  const toast = useToast()
  const queryClient = useQueryClient()
  const tenantId = getActiveTenantId() ?? ''
  // `projectKeys` is invalidated nowhere else, so every mutation must refresh it.
  const refreshProjects = () => {
    queryClient.invalidateQueries({ queryKey: projectKeys.list() })
    queryClient.invalidateQueries({ queryKey: projectKeys.recent() })
    onChanged?.()
  }
  const { deleteItems } = useDeleteRunner({
    onDeleted: (target, outcome, at) => {
      // A project is never split (#801 E1), but record from the answer all the same.
      if (at && !outcome.deletedInSource) undo.recordDeleteResult(at, target.id, target.name, outcome)
    },
    onSettled: () => refreshProjects(),
  })

  const [shareTarget, setShareTarget] = useState<ProjectItem | null>(null)
  const [shareOrgTarget, setShareOrgTarget] = useState<ProjectItem | null>(null)
  const [accessTarget, setAccessTarget] = useState<ProjectItem | null>(null)
  const [internalTarget, setInternalTarget] = useState<ProjectItem | null>(null)
  const [renameTarget, setRenameTarget] = useState<ProjectItem | null>(null)
  const [renameValue, setRenameValue] = useState('')
  const [picker, setPicker] = useState<{ mode: 'move' | 'copy'; target: ProjectItem } | null>(null)
  const [detailsTarget, setDetailsTarget] = useState<ProjectItem | null>(null)
  const [debugTarget, setDebugTarget] = useState<ProjectItem | null>(null)
  const [iconTarget, setIconTarget] = useState<ProjectItem | null>(null)

  const startRename = (p: ProjectItem) => { setRenameValue(projectDisplayName(p.name)); setRenameTarget(p) }
  const commitRename = async () => {
    const p = renameTarget
    const value = renameValue.trim()
    setRenameTarget(null)
    if (!p || !value) return
    // Preserve the `.project` / `.miniapp` suffix — projectDisplayName strips
    // only trailing exts, so the display name is a prefix of the raw folder name.
    const ext = p.name.slice(projectDisplayName(p.name).length)
    const newName = value + ext
    if (newName === p.name) return
    try {
      await filesApi.moveFile(p.folderId, { name: newName })
      toast.success(t('driveView.toast.renamed', { oldName: projectDisplayName(p.name), newName: value, defaultValue: `Renamed to “${value}”` }))
      refreshProjects()
    } catch (e) { toast.error((e as Error)?.message || 'Rename failed') }
  }
  const commitPicker = async (destId: string) => {
    const pk = picker
    setPicker(null)
    if (!pk) return
    const name = projectDisplayName(pk.target.name)
    try {
      if (pk.mode === 'move') {
        await withMoveProgress(toast, t('driveView.toast.moving', { name }), (hooks) => filesApi.moveFile(pk.target.folderId, { parentId: destId }, hooks))
        toast.success(t('driveView.toast.movedOne', { name, defaultValue: `Moved “${name}”` }))
      } else {
        const res = await filesApi.copyFiles([pk.target.folderId], destId)
        // #1397 G4: a copy that landed with some files left out still shows (its per-file reason is the toast)
        if (res.errors?.length) { toast.error(res.errors[0]?.message || 'Copy failed'); if (res.copied?.length) refreshProjects(); return }
        toast.success(t('driveView.toast.copiedOne', { name, defaultValue: `Copied “${name}”` }))
      }
      refreshProjects()
    } catch (e) {
      // #801 E1: a project is never split — one holding items the caller can't
      // change is refused whole (409 `entityRoot`), and says so.
      // A move says it through the ONE move-error text (a lost big-move job included, #1167 S4).
      const refusal = partitionRefusalKey(e, 'move')
      toast.error(pk.mode === 'move' ? moveErrorText(e, t) : refusal ? t(refusal) : (e as Error)?.message || 'Operation failed')
    }
  }
  // Copy-in-place: same parent, `keepBoth` → the server auto-renames the copy
  // (same semantics as DriveView's same-folder paste fast path in paste.ts).
  const duplicateProject = async (p: ProjectItem) => {
    const name = projectDisplayName(p.name)
    try {
      const info = await filesApi.getFileInfo(p.folderId)
      const res = await filesApi.copyFiles([p.folderId], info.parentId, 'keepBoth')
      // #1397 G4: a copy that landed with some files left out still shows (its per-file reason is the toast)
      if (res.errors?.length) { toast.error(res.errors[0]?.message || 'Copy failed'); if (res.copied?.length) refreshProjects(); return }
      toast.success(t('driveView.toast.duplicatedOne', { name, defaultValue: `Made a copy of “${name}”` }))
      refreshProjects()
    } catch (e) { toast.error((e as Error)?.message || 'Copy failed') }
  }
  const downloadProject = async (p: ProjectItem) => {
    try { await filesApi.download([p.folderId], { containsFolder: true, suggestedName: `${p.name}.zip` }) }
    catch (e) { toast.error((e as Error)?.message || 'Download failed') }
  }
  const deleteProject = async (p: ProjectItem) => {
    const name = projectDisplayName(p.name)
    const ok = await confirm({
      title: t('driveView.confirm.deleteTitle'),
      message: t('driveView.confirm.deleteFile', { name }),
      type: 'danger',
      confirmText: t('driveView.menu.delete'),
    })
    if (!ok) return
    // The drive's runner (#781): a project with a big tree runs as a followed
    // job. Undo and the refresh ride its callbacks (below).
    await deleteItems([{ id: p.folderId, name }])
  }

  const handlers: ProjectCardActionHandlers = {
    onShare: setShareTarget,
    onShareOrg: setShareOrgTarget,
    onWhoHasAccess: setAccessTarget,
    ...(isOrg ? { onShareInternally: setInternalTarget } : {}),
    onRename: startRename,
    onDuplicate: duplicateProject,
    onDownload: downloadProject,
    onDetails: setDetailsTarget,
    onDelete: deleteProject,
    onDebugLog: setDebugTarget,
    onEditIcon: setIconTarget,
    // Move/Copy need the injected picker; without it the items don't appear.
    ...(FolderPickerDialog ? {
      onMoveTo: (p: ProjectItem) => setPicker({ mode: 'move', target: p }),
      onCopyTo: (p: ProjectItem) => setPicker({ mode: 'copy', target: p }),
    } : {}),
  }

  const dialogs = (
    <>
      {shareTarget && (
        <ShareLinksDialog
          fileId={shareTarget.folderId}
          fileName={shareTarget.name}
          filePath={shareTarget.path}
          isFolder
          isAppEligible={isMiniappFolder(shareTarget.name)}
          onClose={() => { setShareTarget(null); refreshProjects() }}
        />
      )}

      {/* Share to another Drive — a personal drive can share out to an org too
          (no admin dance on its behalf), so this is NOT org-gated. */}
      {shareOrgTarget && (
        <ShareDialog
          folderId={shareOrgTarget.folderId}
          folderName={projectDisplayName(shareOrgTarget.name)}
          isFolder
          folderPath={shareOrgTarget.path}
          myPermission={shareOrgTarget.access?.canManage ? 'admin' : 'readcopy'}
          canManage={!!shareOrgTarget.access?.canManage}
          onOpenWhoHasAccess={() => { setAccessTarget(shareOrgTarget); setShareOrgTarget(null) }}
          onClose={() => { setShareOrgTarget(null); refreshProjects() }}
        />
      )}

      {/* Who has access — personal drives still share via links / apps / outbound,
          so it's available there with the (empty) People & teams tab hidden. */}
      {accessTarget && (
        <WhoHasAccessDialog
          folderId={accessTarget.folderId}
          folderName={accessTarget.name}
          folderPath={accessTarget.path}
          tenantId={tenantId}
          myPermission={accessTarget.access?.canManage ? 'admin' : 'readcopy'}
          hidePeopleTeams={!isOrg}
          onClose={() => { setAccessTarget(null); refreshProjects() }}
        />
      )}
      {/* Share internally — the drive's own dialog (people, teams and roles of this org). */}
      {internalTarget && (
        <ShareInternallyDialog
          fileId={internalTarget.folderId}
          fileName={internalTarget.name}
          tenantId={tenantId}
          isOrg={isOrg}
          isFolder
          onClose={() => { setInternalTarget(null); refreshProjects() }}
        />
      )}

      {/* Rename modal — rendered inside a ThemedArea, so themed-* resolves. */}
      {renameTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50" onClick={() => setRenameTarget(null)} />
          <div className="relative z-10 w-[340px] rounded-lg shadow-xl themed-bg-secondary p-4">
            <h3 className="text-sm font-semibold themed-text mb-3">{t('driveView.menu.rename', { defaultValue: 'Rename' })}</h3>
            <input
              autoFocus
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') commitRename(); if (e.key === 'Escape') setRenameTarget(null) }}
              onFocus={(e) => e.target.select()}
              className="w-full themed-input rounded-md px-3 py-2 text-sm mb-3"
            />
            <div className="flex justify-end gap-2">
              <button onClick={() => setRenameTarget(null)} className="themed-btn-secondary rounded-md px-3 py-1.5 text-sm">{t('common.cancel', { defaultValue: 'Cancel' })}</button>
              <button onClick={commitRename} className="themed-btn-primary rounded-md px-3 py-1.5 text-sm">{t('driveView.menu.rename', { defaultValue: 'Rename' })}</button>
            </div>
          </div>
        </div>
      )}

      {picker && FolderPickerDialog && (
        <FolderPickerDialog
          tenantId={tenantId}
          excludeIds={[picker.target.folderId]}
          onSelect={commitPicker}
          onClose={() => setPicker(null)}
          title={picker.mode === 'move' ? t('driveView.menu.move', { defaultValue: 'Move to…' }) : t('driveView.menu.copyTo', { defaultValue: 'Copy to…' })}
          confirmLabel={picker.mode === 'move' ? t('driveView.menu.move', { defaultValue: 'Move' }) : t('driveView.menu.copyTo', { defaultValue: 'Copy' })}
        />
      )}

      {detailsTarget && <FileInfoDialog file={asFile(detailsTarget)} onClose={() => setDetailsTarget(null)} />}
      {debugTarget && <DebugLogViewer file={asFile(debugTarget)} onClose={() => setDebugTarget(null)} />}
      {iconTarget && (
        <MiniappIconEditorDialog
          file={{ id: iconTarget.folderId, name: iconTarget.name, metadataJson: iconTarget.metadataJson }}
          tenantId={tenantId || undefined}
          onClose={() => setIconTarget(null)}
          onSaved={refreshProjects}
        />
      )}
    </>
  )

  const open = !!(shareTarget || shareOrgTarget || accessTarget || internalTarget || renameTarget || picker || detailsTarget || debugTarget || iconTarget)
  return { handlers, dialogs, open }
}
